import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib';
import { Box, Typography, Card, CardContent, Alert } from '@mui/material';
import { RocketLaunch, CheckCircle } from '@mui/icons-material';
import LoadingPage from '../components/LoadingPage';

interface LocationState {
  businessIdeaId: string;
  message: string;
}

const ProjectLinkingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const state = location.state as LocationState;
  const [countdown, setCountdown] = useState(5);
  const [showSuccess, setShowSuccess] = useState(false);

  // Redirect if no state provided
  useEffect(() => {
    if (!state?.businessIdeaId) {
      navigate('/home');
      return;
    }

    // Show success state after a brief delay
    const timer = setTimeout(() => {
      setShowSuccess(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [state, navigate]);

  // Countdown timer for redirect
  useEffect(() => {
    if (showSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (showSuccess && countdown === 0) {
      // Redirect to home page
      navigate('/home');
    }
  }, [showSuccess, countdown, navigate]);

  if (!state?.businessIdeaId) {
    return <LoadingPage title="Redirecting..." subtitle="Taking you to your dashboard" />;
  }

  if (!showSuccess) {
    return (
      <LoadingPage
        title="Linking to Project..."
        subtitle="Adding you to the project and setting up your access"
        size="medium"
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%', mx: 'auto' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Success Header */}
          <Box sx={{ mb: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome to the Team! 🎉
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You've been successfully added to the project
            </Typography>
          </Box>

          {/* Project Info */}
          <Box sx={{ mb: 4, textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>
              Project Details
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Project ID:</strong> {state.businessIdeaId}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Your Role:</strong> Collaborator
              </Typography>
              <Typography variant="body2">
                <strong>Access:</strong> Full collaboration access
              </Typography>
            </Box>
          </Box>

          {/* What happens next */}
          <Alert severity="info" sx={{ mb: 3 }}>
            You'll now be able to see this project in your dashboard and collaborate with your team
            members on all tasks and content.
          </Alert>

          {/* Countdown */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Redirecting to your dashboard in {countdown} seconds...
            </Typography>
          </Box>

          {/* Manual redirect button */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Box
              component="button"
              onClick={() => navigate('/home')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              <RocketLaunch fontSize="small" />
              Go to Dashboard
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              You're all set! Welcome to the Orbit collaboration platform.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectLinkingPage;
